// Archive / close-case modal — R-024 OUTCOME CAPTURE at closure (Blueprint §6).
// The WRITE (rpc_archive_case, byte-identical to v_20) + the interim outcome
// encoding live in data/cases.js (archiveCase / encodeArchiveNotes); this module
// is only the dialog UI over them, so any surface — the patient workspace's More
// actions AND the Patients registry row action — opens the SAME modal and the
// same guaranteed semantics apply (legacy openArchiveModal / execArchiveCase).
//
// Captured at closure: a clinical OUTCOME (CLOSURE_OUTCOMES, R-024) + the
// specific archive REASON (ARCHIVE_REASONS, byte-identical enum values) + an
// effective date + mandatory notes. The reason drives a "→ Stage will become …"
// hint (legacy updateArchiveHint) and pre-selects the matching outcome
// (suggestOutcome), which stays user-overridable. A failed save keeps the modal
// + typed notes intact (setBtnBusy KEEP-OPEN-ON-FAIL); success fires onDone.

import { esc } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { openModal, closeModal, setBtnBusy } from '../ui/modal.js';
import { netErr } from '../errors.js';
import { ARCHIVE_REASONS, CLOSURE_OUTCOMES, CLOSED_STAGE_LABELS } from '../db/contract.js';
import { archiveCase, suggestOutcome } from '../data/cases.js';

function doc() {
  return /** @type {any} */ (globalThis).document;
}

/** Today as a local YYYY-MM-DD (legacy todayLocal) — the archive date default + max. */
function todayLocal() {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}

let _wired = false;
let _caseId = /** @type {string|null} */ (null);
/** @type {(() => void)|null} */
let _onDone = null;

/** Test-only: reset module state. @returns {void} */
export function _resetArchiveModal() {
  _wired = false;
  _caseId = null;
  _onDone = null;
}

/**
 * Open the archive / close-case dialog.
 * @param {string} caseId
 * @param {string} name patient name (dialog subtitle)
 * @param {string|null|undefined} ref reference number (subtitle)
 * @param {{onDone?: () => void}} [opts] onDone fires after a landed archive
 * @returns {void}
 */
export function openArchiveModal(caseId, name, ref, opts) {
  if (!caseId) return;
  wireOnce();
  _caseId = caseId;
  _onDone = (opts && opts.onDone) || null;
  const today = todayLocal();
  const reasonOpts =
    '<option value="">— Choose the most accurate reason —</option>' +
    ARCHIVE_REASONS.map(
      (r) => `<option value="${esc(r.value)}" data-leads="${esc(r.leads_to)}">${esc(r.label)}</option>`
    ).join('');
  const outcomeOpts =
    '<option value="">— Select the clinical outcome —</option>' +
    CLOSURE_OUTCOMES.map((o) => `<option value="${esc(o.v)}">${esc(o.l)}</option>`).join('');
  const body =
    '<div class="arch">' +
    '<div class="arch-warn">' +
    '<strong>This will close the case.</strong> All open tasks are cancelled and the patient leaves the active work queues. The full record stays intact for auditing.' +
    '</div>' +
    '<div class="field"><label>Clinical outcome *</label>' +
    `<select id="arch-outcome">${outcomeOpts}</select>` +
    '<div class="arch-hint">What happened to the patient (captured for retention &amp; outcomes reporting).</div></div>' +
    '<div class="field"><label>Reason for archiving *</label>' +
    `<select id="arch-reason">${reasonOpts}</select>` +
    '<div class="arch-hint" id="arch-lead"></div></div>' +
    `<div class="field"><label>Effective date *</label><input id="arch-date" type="date" value="${esc(today)}" max="${esc(today)}"/></div>` +
    '<div class="field"><label>Notes *</label>' +
    '<textarea id="arch-notes" rows="4" placeholder="Brief context: what happened, when, who informed us, any handover steps. For a death, note the date and source; for a doctor-advised stop, the clinical reason."></textarea>' +
    '<div class="arch-hint">Be specific — this becomes part of the permanent record.</div></div>' +
    '</div>';
  openModal(
    'Archive & close case',
    [name, ref].filter(Boolean).join(' · '),
    body,
    [
      { l: 'Cancel', k: 'cancel', fn: () => closeModal() },
      { l: 'Archive case permanently', v: 'danger', k: 'archive', fn: () => void submitArchive() },
    ],
    true
  );
}

/** Reason → "Stage will become …" hint + outcome pre-select (legacy updateArchiveHint). */
function onReasonChange() {
  const d = doc();
  const sel = d && d.getElementById('arch-reason');
  if (!sel) return;
  const val = sel.value;
  const opt = sel.options[sel.selectedIndex];
  const lead = opt && opt.getAttribute ? opt.getAttribute('data-leads') : '';
  const hint = d.getElementById('arch-lead');
  if (hint) {
    hint.innerHTML = lead
      ? `→ Stage will become <strong>${esc(CLOSED_STAGE_LABELS[lead] || lead)}</strong>.`
      : '';
  }
  // Pre-fill the outcome from the reason, but only when the nurse hasn't already
  // chosen one (never stomp an explicit selection).
  const outSel = d.getElementById('arch-outcome');
  if (val && outSel && !outSel.value) outSel.value = suggestOutcome(val);
}

async function submitArchive() {
  const d = doc();
  const caseId = _caseId;
  if (!caseId) return;
  const reason = (d && d.getElementById('arch-reason') && d.getElementById('arch-reason').value) || '';
  const outcome = (d && d.getElementById('arch-outcome') && d.getElementById('arch-outcome').value) || '';
  const date = (d && d.getElementById('arch-date') && d.getElementById('arch-date').value) || todayLocal();
  const notes = ((d && d.getElementById('arch-notes') && d.getElementById('arch-notes').value) || '').trim();
  if (!outcome) {
    toast('Choose the clinical outcome', 'err');
    return;
  }
  if (!reason) {
    toast('Pick a reason', 'err');
    return;
  }
  if (!notes) {
    toast('Notes are required for archiving', 'err');
    return;
  }
  setBtnBusy(true, 'Archiving…', 'archive');
  const res = await archiveCase(caseId, { reason, outcome, notes, date }, undefined);
  if (!res.ok) {
    // KEEP-OPEN-ON-FAIL: preserve the typed notes so a retry loses nothing.
    setBtnBusy(false);
    toast(netErr(res.error, 'Could not archive the case'), 'err');
    return;
  }
  closeModal();
  toast('Case archived & closed', 'ok');
  const done = _onDone;
  _caseId = null;
  _onDone = null;
  if (done) done();
}

function wireOnce() {
  if (_wired) return;
  _wired = true;
  const d = doc();
  if (!d) return;
  d.addEventListener('change', (/** @type {any} */ e) => {
    if (e.target && e.target.id === 'arch-reason') onReasonChange();
  });
}
